import React, { useState, useMemo } from "react";
import {
  Typography,
  Box,
  IconButton,
  Tooltip,
  ClickAwayListener,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import RemoveIcon from "@mui/icons-material/Remove";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import Section from "../../../layout/Section";
import SubSection from "../../../layout/SubSection";
import InputFieldNumber from "../../../common/inputs/InputFieldNumber";
import ToggleSwitch from "../../../common/inputs/ToggleSwitch";
import EditableLabel from "../../../common/inputs/EditableLabel";
import { formatCurrency } from "../../../../utils/formatters";
import { fonts, colors } from "../../../../styles/theme";
import {
  Capex,
  CapexItem,
  CapexSection as CapexSectionType,
  CapexItemCreate,
  CapexItemUpdate,
  GenehmigungCostLine,
  GenehmigungSection as GenehmigungSectionType,
  CAPEX_SECTION_LABELS,
  CAPEX_SECTION_ORDER,
} from "../../../../types";
import ConfirmDialog from "../../../common/feedback/ConfirmDialog";

/**
 * Einzelne CAPEX-Zeile mit editierbarem Label und Eingabefeld
 * Reihenfolge: Bezeichnung → AfA → Toggle → (Preis pro Anlage) → Gesamt
 */
interface CapexRowProps {
  item: CapexItem;
  canEdit: boolean;
  anzahlAnlagen: number;
  onUpdate: (itemId: string, data: CapexItemUpdate) => void;
  onDelete: (itemId: string) => void;
}

const CapexRow: React.FC<CapexRowProps> = ({
  item,
  canEdit,
  anzahlAnlagen,
  onUpdate,
  onDelete,
}) => {
  const isPerUnit = item.is_per_unit || false;
  const preisProAnlage =
    anzahlAnlagen > 0 ? (item.wert || 0) / anzahlAnlagen : 0;

  const handleLabelChange = (value: string) => {
    onUpdate(item.id, { bezeichnung: value });
  };

  const handleAbschreibungsdauerChange = (value: string) => {
    const num = value ? parseFloat(value) : 16;
    onUpdate(item.id, { abschreibungsdauer: isNaN(num) ? 16 : num });
  };

  const handleToggleChange = (newMode: string) => {
    const newIsPerUnit = newMode === "pro_anlage";
    onUpdate(item.id, { is_per_unit: newIsPerUnit });
  };

  const handlePerUnitChange = (value: string) => {
    const num = value ? parseFloat(value) : 0;
    const perUnit = isNaN(num) ? 0 : num;
    onUpdate(item.id, { wert: perUnit * anzahlAnlagen });
  };

  const handleTotalChange = (value: string) => {
    const num = value ? parseFloat(value) : 0;
    onUpdate(item.id, { wert: isNaN(num) ? 0 : num });
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        py: 0.5,
        ml: "-42px",
      }}
    >
      {/* Löschen-Button */}
      <Box sx={{ width: 40, display: "flex", justifyContent: "center" }}>
        {item.is_editable && canEdit ? (
          <Tooltip title="Position entfernen">
            <IconButton
              size="small"
              onClick={() => onDelete(item.id)}
              sx={{
                color: colors.icon.subtle,
                "&:hover": { color: "error.main" },
              }}
            >
              <RemoveIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : (
          <Box sx={{ width: 40 }} />
        )}
      </Box>

      {/* Bezeichnung */}
      <Tooltip title="Bezeichnung" placement="top" arrow>
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <EditableLabel
            value={item.bezeichnung}
            editable={item.is_editable && canEdit}
            minWidth={200}
            onSave={handleLabelChange}
          />
        </Box>
      </Tooltip>

      {/* AfA */}
      <Tooltip title="Abschreibungsdauer" placement="top" arrow>
        <Box sx={{ width: 140 }}>
          <InputFieldNumber
            label=""
            value={String(item.abschreibungsdauer || 16)}
            onChange={handleAbschreibungsdauerChange}
            readOnly={!canEdit}
            suffix="Jahre AfA"
            allowDecimals={false}
          />
        </Box>
      </Tooltip>

      {/* Toggle: Gesamt / Pro Anlage */}
      <Tooltip title="Eingabemodus" placement="top" arrow>
        <Box>
          <ToggleSwitch
            value={isPerUnit ? "pro_anlage" : "gesamt"}
            options={[
              { value: "pro_anlage", label: "Pro Anlage" },
              { value: "gesamt", label: "Gesamt" },
            ]}
            onChange={handleToggleChange}
            disabled={!canEdit}
          />
        </Box>
      </Tooltip>

      {/* Kosten pro Anlage (immer sichtbar, readonly abhängig vom Toggle) */}
      <Tooltip title="Kosten pro Anlage" placement="top" arrow>
        <Box sx={{ width: 180 }}>
          <InputFieldNumber
            label=""
            value={String(preisProAnlage)}
            onChange={handlePerUnitChange}
            readOnly={!canEdit || !isPerUnit}
            placeholder="Preis pro Anlage"
            suffix="€"
            allowDecimals={false}
          />
        </Box>
      </Tooltip>

      {/* Gesamtpreis (readonly wenn "Pro Anlage" aktiv) */}
      <Tooltip title="Gesamtkosten" placement="top" arrow>
        <Box sx={{ width: 180, display: "flex", justifyContent: "flex-end" }}>
          <InputFieldNumber
            label=""
            value={String(item.wert || 0)}
            onChange={handleTotalChange}
            readOnly={!canEdit || isPerUnit}
            suffix="€"
            allowDecimals={false}
          />
        </Box>
      </Tooltip>
    </Box>
  );
};

/**
 * Unterabschnitt mit + Button zum Hinzufügen
 */
interface CapexSubSectionProps {
  section: CapexSectionType;
  items: CapexItem[];
  totalCost: number;
  canEdit: boolean;
  anzahlAnlagen: number;
  onUpdate: (itemId: string, data: CapexItemUpdate) => void;
  onDelete: (itemId: string) => void;
  onAdd: (data: CapexItemCreate) => void;
  customLabel?: string;
  onLabelChange?: (newLabel: string) => void;
}

const CapexSubSection: React.FC<CapexSubSectionProps> = ({
  section,
  items,
  totalCost,
  canEdit,
  anzahlAnlagen,
  onUpdate,
  onDelete,
  onAdd,
  customLabel,
  onLabelChange,
}) => {
  const isLocked = section === "windenergieanlage";
  const displayLabel = customLabel ?? CAPEX_SECTION_LABELS[section] ?? section;

  const handleAddItem = () => {
    onAdd({
      section,
      bezeichnung: "Neue Position",
      wert: 0,
    });
  };

  return (
    <SubSection title={displayLabel} totalCost={formatCurrency(totalCost)}>
      {items.map((item) => (
        <CapexRow
          key={item.id}
          item={item}
          canEdit={canEdit}
          anzahlAnlagen={anzahlAnlagen}
          onUpdate={onUpdate}
          onDelete={onDelete}
        />
      ))}

      {/* Hinzufügen-Button - bündig mit den Mülleimer-Icons */}
      {canEdit && (
        <Box sx={{ mt: 1, ml: "-42px", display: "flex" }}>
          <Box sx={{ width: 40, display: "flex", justifyContent: "center" }}>
            <Tooltip title="Position anlegen">
              <IconButton
                size="small"
                onClick={handleAddItem}
                sx={{
                  color: "primary.main",
                  "&:hover": {
                    bgcolor: "primary.light",
                    color: "primary.contrastText",
                  },
                }}
              >
                <AddIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      )}
    </SubSection>
  );
};

/**
 * Abgeleiteter Unterabschnitt "Genehmigung".
 *
 * Die Positionen kommen aus dem Genehmigungsverfahren und sind read-only – es gibt keine
 * capex_items dazu und deshalb auch keinen Schreibpfad. Editierbar ist ausschließlich die
 * Summe in der Überschriftzeile: der Kalkulations-Platzhalter für den Fall, dass man rechnen
 * will, ohne das Verfahren vollständig zu pflegen. Ist er gesetzt, weist ein Warnzeichen
 * darauf hin und bietet an, wieder auf die Planwerte aus dem Verfahren zurückzugehen.
 */
interface GenehmigungSubSectionProps {
  genehmigung: GenehmigungSectionType;
  canEdit: boolean;
  customLabel?: string;
  onOverrideChange: (wert: number | null) => void;
}

const GenehmigungSubSection: React.FC<GenehmigungSubSectionProps> = ({
  genehmigung,
  canEdit,
  customLabel,
  onOverrideChange,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState("");
  const [resetDialogOpen, setResetDialogOpen] = useState(false);

  const displayLabel = customLabel ?? CAPEX_SECTION_LABELS.genehmigung;

  // Gruppierung nur, wenn das Projekt tatsächlich mehrere Verfahren hat – bei einem
  // Verfahren wäre eine Zwischenüberschrift nur Rauschen.
  const gruppen = useMemo(() => {
    const map = new Map<
      string,
      { label: string; items: GenehmigungCostLine[] }
    >();
    for (const item of genehmigung.items) {
      const key = item.permit_procedure_id;
      if (!map.has(key)) {
        map.set(key, {
          label: item.permit_procedure_bezeichnung || "Genehmigungsverfahren",
          items: [],
        });
      }
      map.get(key)!.items.push(item);
    }
    return Array.from(map.values());
  }, [genehmigung.items]);

  const handleStartEdit = () => {
    setEditValue(String(genehmigung.summe));
    setIsEditing(true);
  };

  const handleSaveEdit = () => {
    const num = parseFloat(editValue);
    onOverrideChange(isNaN(num) ? null : num);
    setIsEditing(false);
  };

  const handleReset = () => {
    onOverrideChange(null);
    setResetDialogOpen(false);
  };

  const titleNode = (
    <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
      <Typography
        variant="h6"
        sx={{
          fontWeight: fonts.weights.medium,
          textTransform: "none",
          fontSize: "1.1rem",
        }}
      >
        {displayLabel}
      </Typography>
      <Box sx={{ flexGrow: 1 }} />

      {genehmigung.ist_uebersteuert && (
        <Tooltip title="Summe manuell überschrieben – Planwerte aus dem Genehmigungsverfahren übernehmen">
          <IconButton
            size="small"
            onClick={() => setResetDialogOpen(true)}
            sx={{ color: colors.status.warning }}
          >
            <WarningAmberIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      {canEdit && !isEditing && (
        <Tooltip title="Gesamtsumme manuell setzen">
          <IconButton
            size="small"
            onClick={handleStartEdit}
            sx={{ color: colors.icon.subtle }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      {isEditing && (
        <ClickAwayListener onClickAway={handleSaveEdit}>
          <Box sx={{ width: 180 }}>
            <InputFieldNumber
              label=""
              value={editValue}
              onChange={setEditValue}
              suffix="€"
              allowDecimals={false}
            />
          </Box>
        </ClickAwayListener>
      )}
    </Box>
  );

  return (
    <>
      <SubSection
        title={displayLabel}
        titleNode={titleNode}
        totalCost={isEditing ? undefined : formatCurrency(genehmigung.summe)}
      >
        {genehmigung.items.length === 0 ? (
          <Typography sx={{ color: colors.text.secondary, py: 1 }}>
            Noch keine Kostenpositionen im Genehmigungsverfahren erfasst.
          </Typography>
        ) : (
          gruppen.map((gruppe, index) => (
            <Box key={index} sx={{ mb: gruppen.length > 1 ? 2 : 0 }}>
              {gruppen.length > 1 && (
                <Typography
                  sx={{
                    fontWeight: fonts.weights.medium,
                    color: colors.text.secondary,
                    fontSize: "0.9rem",
                    mb: 0.5,
                  }}
                >
                  {gruppe.label}
                </Typography>
              )}
              {gruppe.items.map((item, itemIndex) => (
                <Box
                  key={itemIndex}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    py: 0.5,
                  }}
                >
                  <Typography sx={{ flex: 1, minWidth: 200 }}>
                    {item.bezeichnung}
                  </Typography>
                  <Typography
                    sx={{
                      width: 180,
                      textAlign: "right",
                      fontFamily: fonts.monospace,
                      color: colors.text.secondary,
                    }}
                  >
                    {formatCurrency(item.wert)}
                  </Typography>
                </Box>
              ))}
            </Box>
          ))
        )}

        {genehmigung.ist_uebersteuert && (
          <Typography
            sx={{ color: colors.text.secondary, fontSize: "0.85rem", mt: 2 }}
          >
            In der Kalkulation zählt die manuell gesetzte Summe von{" "}
            {formatCurrency(genehmigung.summe)}; aus dem Genehmigungsverfahren
            abgeleitet wären {formatCurrency(genehmigung.summe_abgeleitet)}.
          </Typography>
        )}
      </SubSection>

      <ConfirmDialog
        open={resetDialogOpen}
        title="Planwerte übernehmen"
        message={
          `Die manuell gesetzte Summe von ${formatCurrency(genehmigung.summe)} wird verworfen. ` +
          `Der Abschnitt rechnet danach wieder mit ${formatCurrency(genehmigung.summe_abgeleitet)} ` +
          `aus dem Genehmigungsverfahren.`
        }
        confirmLabel="Planwerte übernehmen"
        onConfirm={handleReset}
        onCancel={() => setResetDialogOpen(false)}
      />
    </>
  );
};

interface CapexSectionProps {
  capex: Capex | null;
  canEdit: boolean;
  anzahlAnlagen: number;
  onItemUpdate: (itemId: string, data: CapexItemUpdate) => void;
  onItemDelete: (itemId: string) => void;
  onItemCreate: (data: CapexItemCreate) => void;
  onSectionLabelChange?: (sectionKey: string, label: string) => void;
  onGenehmigungOverrideChange?: (wert: number | null) => void;
}

/**
 * CAPEX Section - Investitionskosten
 * Item-basiertes Modell mit editierbaren Bezeichnungen
 */
const CapexSection: React.FC<CapexSectionProps> = ({
  capex,
  canEdit,
  anzahlAnlagen,
  onItemUpdate,
  onItemDelete,
  onItemCreate,
  onSectionLabelChange,
  onGenehmigungOverrideChange,
}) => {
  // Items nach Section gruppieren
  const itemsBySection = useMemo(() => {
    // "genehmigung" fehlt bewusst: der Abschnitt hat keine capex_items.
    const map: Record<Exclude<CapexSectionType, "genehmigung">, CapexItem[]> = {
      windenergieanlage: [],
      infrastruktur: [],
      planung_genehmigung: [],
      bauueberwachung: [],
    };

    if (capex?.items) {
      for (const item of capex.items) {
        const section = item.section as Exclude<
          CapexSectionType,
          "genehmigung"
        >;
        if (map[section]) {
          map[section].push(item);
        }
      }

      // Sortieren nach sort_order
      for (const section of CAPEX_SECTION_ORDER) {
        map[section].sort((a, b) => a.sort_order - b.sort_order);
      }
    }

    return map;
  }, [capex?.items]);

  // Summen pro Section berechnen
  const sectionSums = useMemo(() => {
    const sums: Record<Exclude<CapexSectionType, "genehmigung">, number> = {
      windenergieanlage: 0,
      infrastruktur: 0,
      planung_genehmigung: 0,
      bauueberwachung: 0,
    };

    for (const section of CAPEX_SECTION_ORDER) {
      sums[section] = itemsBySection[section].reduce(
        (sum, item) => sum + (item.wert || 0),
        0,
      );
    }

    return sums;
  }, [itemsBySection]);

  // Gesamtsumme inkl. des abgeleiteten Genehmigungs-Abschnitts – dessen Wert steckt nicht in
  // `items`, muss aber in die Investitionssumme einfließen.
  const summeGesamt = useMemo(() => {
    const summeItems = CAPEX_SECTION_ORDER.reduce(
      (sum, section) => sum + sectionSums[section],
      0,
    );
    return summeItems + (capex?.genehmigung?.summe ?? 0);
  }, [sectionSums, capex?.genehmigung?.summe]);

  return (
    <Section
      title="Investitionskosten"
      actions={
        <Typography
          sx={{
            fontWeight: fonts.weights.semiBold,
            fontSize: "1rem",
            color: colors.accent.teal,
            fontFamily: fonts.monospace,
          }}
        >
          {formatCurrency(summeGesamt)}
        </Typography>
      }
    >
      {CAPEX_SECTION_ORDER.map((section) => (
        <React.Fragment key={section}>
          <CapexSubSection
            section={section}
            items={itemsBySection[section]}
            totalCost={sectionSums[section]}
            canEdit={canEdit}
            anzahlAnlagen={anzahlAnlagen}
            onUpdate={onItemUpdate}
            onDelete={onItemDelete}
            onAdd={onItemCreate}
            customLabel={capex?.section_labels?.[section]}
            onLabelChange={
              onSectionLabelChange
                ? (label) => onSectionLabelChange(section, label)
                : undefined
            }
          />

          {/* Der abgeleitete Abschnitt steht direkt hinter "Planung", weil er sie inhaltlich
              fortsetzt – er ist kein eigener Eintrag in CAPEX_SECTION_ORDER, da er keine
              capex_items hat. */}
          {section === "planung_genehmigung" && capex?.genehmigung && (
            <GenehmigungSubSection
              genehmigung={capex.genehmigung}
              canEdit={canEdit}
              customLabel={capex.section_labels?.genehmigung}
              onOverrideChange={onGenehmigungOverrideChange ?? (() => {})}
            />
          )}
        </React.Fragment>
      ))}
    </Section>
  );
};

export default React.memo(CapexSection);
